import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  UserGroupIcon,
  ChartBarIcon,
  CogIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { employeeService } from '../services/employeeService';
import LeadAssignmentService from '../services/leadAssignmentService';

const BulkLeadAssignmentModal = ({
  isOpen,
  onClose,
  selectedLeads = [],
  onAssignmentComplete
}) => {
  // State management
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [distributionMethod, setDistributionMethod] = useState('equal');
  const [priority, setPriority] = useState('medium');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [distributionPreview, setDistributionPreview] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'available', 'active'
  const [filterDepartment, setFilterDepartment] = useState('all'); // 'all' or department name
  const [sortBy, setSortBy] = useState('name'); // 'name', 'workload', 'performance'
  const [departments, setDepartments] = useState([]); // Available departments
  const [leadsToAssign, setLeadsToAssign] = useState([]); // Leads that will be assigned
  const [showLeadDetails, setShowLeadDetails] = useState(true); // Toggle lead details view
  const [assignmentMode, setAssignmentMode] = useState('replace'); // 'replace' or 'add' - NEW!

  // Load employees on modal open
  useEffect(() => {
    if (isOpen) {
      loadEmployees();
      resetForm();
      // Initialize leadsToAssign - exclude already assigned leads by default
      setLeadsToAssign(selectedLeads.map(lead => {
        const isAssigned = (lead.assignedEmployees && lead.assignedEmployees.length > 0) || 
                          lead.assignedEmployeeName;
        return { 
          ...lead, 
          include: !isAssigned // Unassigned leads are included by default
        };
      }));
    }
  }, [isOpen, selectedLeads]);

  // Generate preview when employees or method changes
  useEffect(() => {
    const includedLeads = leadsToAssign.filter(l => l.include);
    if (selectedEmployees.length > 0 && includedLeads.length > 0) {
      generateDistributionPreview();
    }
  }, [selectedEmployees, distributionMethod, leadsToAssign]);

  const resetForm = () => {
    setSelectedEmployees([]);
    setDistributionMethod('equal');
    setPriority('medium');
    setNotes('');
    setShowPreview(false);
    setDistributionPreview([]);
    setError('');
    // Reset leads - exclude already assigned leads by default
    setLeadsToAssign(selectedLeads.map(lead => {
      const isAssigned = (lead.assignedEmployees && lead.assignedEmployees.length > 0) || 
                        lead.assignedEmployeeName;
      return { 
        ...lead, 
        include: !isAssigned // Unassigned leads are included by default
      };
    }));
  };

  const toggleLeadInclusion = (leadId) => {
    setLeadsToAssign(prev => prev.map(lead => 
      lead._id === leadId ? { ...lead, include: !lead.include } : lead
    ));
  };

  const toggleAllLeads = () => {
    const allIncluded = leadsToAssign.every(l => l.include);
    setLeadsToAssign(prev => prev.map(lead => ({ ...lead, include: !allIncluded })));
  };

  const selectOnlyUnassigned = () => {
    setLeadsToAssign(prev => prev.map(lead => {
      const isAssigned = (lead.assignedEmployees && lead.assignedEmployees.length > 0) || 
                        lead.assignedEmployeeName;
      return { ...lead, include: !isAssigned };
    }));
  };

  const selectOnlyAssigned = () => {
    setLeadsToAssign(prev => prev.map(lead => {
      const isAssigned = (lead.assignedEmployees && lead.assignedEmployees.length > 0) || 
                        lead.assignedEmployeeName;
      return { ...lead, include: isAssigned };
    }));
  };

  const getAssignedEmployeeName = (lead) => {
    if (lead.assignedEmployees && lead.assignedEmployees.length > 0) {
      return lead.assignedEmployees.map(emp => emp.name).join(', ');
    }
    if (lead.assignedEmployeeName) {
      return lead.assignedEmployeeName;
    }
    return 'Unassigned';
  };

  const loadEmployees = async () => {
    setLoadingData(true);
    setError('');

    try {
      const result = await employeeService.getAllEmployees();

      if (result.success) {
        // Enhance employee data with mock statistics for demonstration
        const enhancedEmployees = (result.data || []).map(emp => ({
          ...emp,
          currentWorkload: Math.floor(Math.random() * 50) + 10, // Mock current leads count
          performanceScore: Math.floor(Math.random() * 30) + 70, // Mock performance 70-100
          isAvailable: Math.random() > 0.2, // 80% availability
          lastAssignedDate: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000),
          // Keep original department object from backend
          role: emp.role || 'Sales Executive'
        }));

        setEmployees(enhancedEmployees);

        // Extract unique departments - handle array format
        const uniqueDepts = [...new Set(enhancedEmployees.map(emp => {
          // Department is an array in database, get first element
          if (Array.isArray(emp.department) && emp.department.length > 0) {
            const dept = emp.department[0];
            if (dept?.name) return dept.name;
            if (typeof dept === 'string') return dept;
          }
          if (emp.department?.name) return emp.department.name;
          if (typeof emp.department === 'string') return emp.department;
          return 'No Department';
        }))].sort();
        console.log('📊 Unique departments found:', uniqueDepts);
        console.log('👥 Sample employee with department:', enhancedEmployees[0]);
        setDepartments(uniqueDepts);
      } else {
        setError('Failed to load employees');
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      setError('Failed to load employee data');
    }

    setLoadingData(false);
  };

  const generateDistributionPreview = () => {
    const includedLeads = leadsToAssign.filter(l => l.include);
    
    if (selectedEmployees.length === 0 || includedLeads.length === 0) {
      setDistributionPreview([]);
      return;
    }

    let distribution = [];
    const totalLeads = includedLeads.length;
    const totalEmployees = selectedEmployees.length;

    switch (distributionMethod) {
      case 'equal':
        // Equal distribution
        const leadsPerEmployee = Math.floor(totalLeads / totalEmployees);
        const remainder = totalLeads % totalEmployees;

        selectedEmployees.forEach((emp, index) => {
          const assignedCount = leadsPerEmployee + (index < remainder ? 1 : 0);
          distribution.push({
            employee: emp,
            assignedCount,
            percentage: ((assignedCount / totalLeads) * 100).toFixed(1)
          });
        });
        break;

      case 'weighted':
        // Weighted by performance and availability
        const weights = selectedEmployees.map(emp => {
          const performanceWeight = emp.performanceScore / 100;
          const availabilityWeight = emp.isAvailable ? 1 : 0.5;
          const workloadWeight = Math.max(0.1, (100 - emp.currentWorkload) / 100);
          return performanceWeight * availabilityWeight * workloadWeight;
        });

        const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);

        selectedEmployees.forEach((emp, index) => {
          const assignedCount = Math.round((weights[index] / totalWeight) * totalLeads);
          distribution.push({
            employee: emp,
            assignedCount,
            percentage: ((assignedCount / totalLeads) * 100).toFixed(1)
          });
        });
        break;

      case 'balanced':
        // Balanced by current workload
        const maxWorkload = Math.max(...selectedEmployees.map(emp => emp.currentWorkload));
        const balanceWeights = selectedEmployees.map(emp => maxWorkload - emp.currentWorkload + 1);
        const totalBalanceWeight = balanceWeights.reduce((sum, weight) => sum + weight, 0);

        selectedEmployees.forEach((emp, index) => {
          const assignedCount = Math.round((balanceWeights[index] / totalBalanceWeight) * totalLeads);
          distribution.push({
            employee: emp,
            assignedCount,
            percentage: ((assignedCount / totalLeads) * 100).toFixed(1)
          });
        });
        break;

      default:
        distribution = [];
    }

    // Ensure total adds up to selectedLeads.length
    const totalAssigned = distribution.reduce((sum, item) => sum + item.assignedCount, 0);
    if (totalAssigned !== totalLeads && distribution.length > 0) {
      const diff = totalLeads - totalAssigned;
      distribution[0].assignedCount += diff;
      distribution[0].percentage = ((distribution[0].assignedCount / totalLeads) * 100).toFixed(1);
    }

    setDistributionPreview(distribution);
  };

  const handleEmployeeToggle = (employee) => {
    setSelectedEmployees(prev => {
      const isSelected = prev.some(emp => emp._id === employee._id);
      if (isSelected) {
        return prev.filter(emp => emp._id !== employee._id);
      } else {
        return [...prev, employee];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredEmployees = getFilteredEmployees();
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees);
    }
  };

  const getFilteredEmployees = () => {
    let filtered = employees;

    // Apply status filter
    switch (filterStatus) {
      case 'available':
        filtered = filtered.filter(emp => emp.isAvailable);
        break;
      case 'active':
        filtered = filtered.filter(emp => emp.currentWorkload < 40);
        break;
      default:
        // 'all' - no filtering
        break;
    }

    // Apply department filter - handle array format
    if (filterDepartment !== 'all') {
      filtered = filtered.filter(emp => {
        let empDept = 'No Department';
        // Department is an array, get first element
        if (Array.isArray(emp.department) && emp.department.length > 0) {
          const dept = emp.department[0];
          empDept = dept?.name || dept || 'No Department';
        } else if (emp.department?.name) {
          empDept = emp.department.name;
        } else if (typeof emp.department === 'string') {
          empDept = emp.department;
        }
        return empDept === filterDepartment;
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'workload':
        filtered = [...filtered].sort((a, b) => a.currentWorkload - b.currentWorkload);
        break;
      case 'performance':
        filtered = [...filtered].sort((a, b) => b.performanceScore - a.performanceScore);
        break;
      case 'name':
      default:
        filtered = [...filtered].sort((a, b) =>
          (a.teamMemberName || a.name || '').localeCompare(b.teamMemberName || b.name || '')
        );
        break;
    }

    return filtered;
  };

  const handleBulkAssignment = async () => {
    const includedLeads = leadsToAssign.filter(l => l.include);
    
    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee');
      return;
    }

    if (includedLeads.length === 0) {
      setError('Please select at least one lead to assign');
      return;
    }

    if (distributionPreview.length === 0) {
      setError('No distribution preview available');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create assignment batches based on distribution
      const assignments = [];
      let leadIndex = 0;

      for (const item of distributionPreview) {
        if (item.assignedCount > 0) {
          const leadsForEmployee = includedLeads.slice(leadIndex, leadIndex + item.assignedCount);

          for (const lead of leadsForEmployee) {
            assignments.push({
              leadId: lead._id,
              employeeId: item.employee._id,
              employeeName: item.employee.teamMemberName || item.employee.name,
              priority,
              notes: notes || `Bulk assignment - ${distributionMethod} distribution`,
              assignedDate: new Date().toISOString(),
              distributionMethod,
              assignmentMode: assignmentMode // 'replace' or 'add'
            });
          }

          leadIndex += item.assignedCount;
        }
      }

      // Execute bulk assignment with enhanced distribution
      const result = await LeadAssignmentService.bulkAssignLeadsWithDistribution(assignments);

      if (result.success) {
        onAssignmentComplete && onAssignmentComplete({
          success: true,
          message: `Successfully assigned ${includedLeads.length} leads to ${selectedEmployees.length} employees`,
          assignments: assignments
        });
        onClose();
      } else {
        setError(result.message || 'Failed to assign leads');
      }
    } catch (error) {
      console.error('Error in bulk assignment:', error);
      setError('Failed to assign leads. Please try again.');
    }

    setLoading(false);
  };

  if (!isOpen) return null;

  const filteredEmployees = getFilteredEmployees();
  const allSelected = selectedEmployees.length === filteredEmployees.length && filteredEmployees.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden relative z-[10000] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="flex items-center space-x-3">
            <UserGroupIcon className="h-6 w-6" />
            <div>
              <h2 className="text-xl font-bold">Bulk Lead Assignment</h2>
              <p className="text-blue-100 text-sm">
                Assign {selectedLeads.length} leads to multiple employees
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Lead Details Panel - Collapsible */}
          {showLeadDetails && (
            <div className="w-80 border-r border-gray-200 flex flex-col bg-gray-50">
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">Selected Leads</h3>
                  <button
                    onClick={() => setShowLeadDetails(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                
                {/* Lead Count and Stats */}
                <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <span className="font-semibold text-blue-900">
                        {leadsToAssign.filter(l => l.include).length}
                      </span>
                      <span className="text-blue-700"> of {leadsToAssign.length} leads selected</span>
                    </div>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">
                      ✓ {leadsToAssign.filter(l => !l.include && ((l.assignedEmployees && l.assignedEmployees.length > 0) || l.assignedEmployeeName)).length} Assigned (Excluded)
                    </span>
                    <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                      ○ {leadsToAssign.filter(l => l.include && !((l.assignedEmployees && l.assignedEmployees.length > 0) || l.assignedEmployeeName)).length} Unassigned (Included)
                    </span>
                  </div>
                </div>

                {/* Quick Filter Buttons */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <button
                    onClick={toggleAllLeads}
                    className="text-xs px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                  >
                    {leadsToAssign.every(l => l.include) ? 'Deselect All' : 'Select All'}
                  </button>
                  <button
                    onClick={selectOnlyUnassigned}
                    className="text-xs px-2 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                  >
                    Only Unassigned
                  </button>
                  <button
                    onClick={selectOnlyAssigned}
                    className="text-xs px-2 py-1.5 bg-yellow-600 text-white rounded hover:bg-yellow-700 font-medium"
                  >
                    Only Assigned
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3">
                <div className="space-y-2">
                  {leadsToAssign.map((lead) => {
                    const assignedTo = getAssignedEmployeeName(lead);
                    const isAssigned = assignedTo !== 'Unassigned';
                    
                    return (
                      <div
                        key={lead._id}
                        onClick={() => toggleLeadInclusion(lead._id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          lead.include
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 bg-gray-100 opacity-70'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            lead.include ? 'bg-blue-600 border-blue-600' : 'border-gray-400 bg-white'
                          }`}>
                            {lead.include && (
                              <CheckCircleIcon className="h-3 w-3 text-white" />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm truncate">
                              {lead.name}
                            </div>
                            <div className="text-xs text-gray-600 truncate">
                              {lead.email || 'No email'}
                            </div>
                            
                            {/* Current Assignment Status */}
                            {isAssigned ? (
                              <div className="mt-2">
                                <div className="flex items-center gap-1 text-xs">
                                  <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded font-medium">
                                    <UserGroupIcon className="h-3 w-3" />
                                    <span>Already Assigned</span>
                                  </div>
                                </div>
                                <div className="mt-1 text-xs text-gray-700 truncate" title={assignedTo}>
                                  👤 {assignedTo}
                                </div>
                                {!lead.include && (
                                  <div className="mt-1 text-xs text-orange-600 font-medium">
                                    ⚠️ Excluded by default
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="mt-2">
                                <div className="flex items-center gap-1 text-xs">
                                  <div className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                                    <span>✓ Unassigned</span>
                                  </div>
                                </div>
                                {lead.include && (
                                  <div className="mt-1 text-xs text-green-600 font-medium">
                                    ✓ Ready to assign
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Lead Details */}
                            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                              {lead.status && (
                                <span className="bg-gray-100 px-1.5 py-0.5 rounded">
                                  {lead.status}
                                </span>
                              )}
                              {lead.budget && (
                                <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                  ₹{lead.budget}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Toggle Button when Lead Details is Hidden */}
          {!showLeadDetails && (
            <button
              onClick={() => setShowLeadDetails(true)}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-blue-600 text-white p-2 rounded-r-lg shadow-lg hover:bg-blue-700 z-10"
              title="Show lead details"
            >
              <UserGroupIcon className="h-5 w-5" />
            </button>
          )}

          {/* Left Panel - Employee Selection */}
          <div className="w-1/2 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Employees</h3>

              {/* Filters and Controls */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filter Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All Employees</option>
                    <option value="available">Available Only</option>
                    <option value="active">Low Workload</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <select
                    value={filterDepartment}
                    onChange={(e) => setFilterDepartment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="all">All Departments</option>
                    {departments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sort By
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="name">Name</option>
                    <option value="workload">Current Workload</option>
                    <option value="performance">Performance Score</option>
                  </select>
                </div>
              </div>

              {/* Select All Button */}
              <button
                onClick={handleSelectAll}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                {allSelected ? 'Deselect All' : 'Select All'} ({filteredEmployees.length})
              </button>

              <div className="mt-2 text-sm text-gray-600">
                Selected: {selectedEmployees.length} / {filteredEmployees.length} employees
              </div>
            </div>

            {/* Employee List */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingData ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <UserGroupIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No employees found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredEmployees.map((employee) => {
                    const isSelected = selectedEmployees.some(emp => emp._id === employee._id);
                    return (
                      <div
                        key={employee._id}
                        onClick={() => handleEmployeeToggle(employee)}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                }`}>
                                {isSelected && (
                                  <CheckCircleIcon className="h-3 w-3 text-white" />
                                )}
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900">
                                  {employee.teamMemberName || employee.name}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {employee.role} • {(() => {
                                    if (Array.isArray(employee.department) && employee.department.length > 0) {
                                      const dept = employee.department[0];
                                      return dept?.name || dept || 'No Department';
                                    }
                                    if (employee.department?.name) return employee.department.name;
                                    if (typeof employee.department === 'string') return employee.department;
                                    return 'No Department';
                                  })()}
                                </p>
                              </div>
                            </div>

                            <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">Workload:</span>
                                <div className="flex items-center mt-1">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                                    <div
                                      className={`h-2 rounded-full ${employee.currentWorkload > 40 ? 'bg-red-500' :
                                        employee.currentWorkload > 25 ? 'bg-yellow-500' : 'bg-green-500'
                                        }`}
                                      style={{ width: `${Math.min(employee.currentWorkload * 2, 100)}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs font-medium">
                                    {employee.currentWorkload}
                                  </span>
                                </div>
                              </div>

                              <div>
                                <span className="text-gray-500">Performance:</span>
                                <div className="text-sm font-medium mt-1">
                                  {employee.performanceScore}%
                                </div>
                              </div>

                              <div>
                                <span className="text-gray-500">Status:</span>
                                <div className="flex items-center mt-1">
                                  <div className={`w-2 h-2 rounded-full mr-2 ${employee.isAvailable ? 'bg-green-500' : 'bg-red-500'
                                    }`}></div>
                                  <span className="text-xs">
                                    {employee.isAvailable ? 'Available' : 'Busy'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Distribution Settings & Preview */}
          <div className="w-1/2 flex flex-col">
            <div className="p-4 border-b border-gray-200 bg-gray-50 overflow-y-auto max-h-[500px]">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribution Settings</h3>

              {/* Distribution Method */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Distribution Method
                </label>
                <div className="grid grid-cols-1 gap-2">
                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="distributionMethod"
                      value="equal"
                      checked={distributionMethod === 'equal'}
                      onChange={(e) => setDistributionMethod(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium">Equal Distribution</div>
                      <div className="text-sm text-gray-600">Distribute leads equally among selected employees</div>
                    </div>
                  </label>

                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="distributionMethod"
                      value="weighted"
                      checked={distributionMethod === 'weighted'}
                      onChange={(e) => setDistributionMethod(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium">Performance Weighted</div>
                      <div className="text-sm text-gray-600">Distribute based on performance and availability</div>
                    </div>
                  </label>

                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="distributionMethod"
                      value="balanced"
                      checked={distributionMethod === 'balanced'}
                      onChange={(e) => setDistributionMethod(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium">Workload Balanced</div>
                      <div className="text-sm text-gray-600">Balance based on current workload</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Assignment Mode - NEW! */}
              <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg">
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  📋 Assignment Mode
                </label>
                <div className="grid grid-cols-1 gap-2">
                  <label className="flex items-start p-3 border-2 rounded-lg cursor-pointer hover:bg-white transition-all bg-white">
                    <input
                      type="radio"
                      name="assignmentMode"
                      value="replace"
                      checked={assignmentMode === 'replace'}
                      onChange={(e) => setAssignmentMode(e.target.value)}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 flex items-center gap-2">
                        🔄 Replace Assignment
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Default</span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Remove old employees and assign to new ones
                      </div>
                      <div className="text-xs text-gray-500 mt-1 italic">
                        Example: Lead assigned to A → Assign to B → Lead now only with B
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start p-3 border-2 rounded-lg cursor-pointer hover:bg-white transition-all bg-white">
                    <input
                      type="radio"
                      name="assignmentMode"
                      value="add"
                      checked={assignmentMode === 'add'}
                      onChange={(e) => setAssignmentMode(e.target.value)}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 flex items-center gap-2">
                        ➕ Add to Assignment
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Team Mode</span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Keep old employees and add new ones (Team collaboration)
                      </div>
                      <div className="text-xs text-gray-500 mt-1 italic">
                        Example: Lead assigned to A → Assign to B → Lead now with A + B
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Priority and Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Assignment notes..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Distribution Preview */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-semibold text-gray-900">Distribution Preview</h4>
                <ChartBarIcon className="h-5 w-5 text-gray-500" />
              </div>

              {selectedEmployees.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <InformationCircleIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Select employees to see distribution preview</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {distributionPreview.map((item, index) => (
                    <div key={item.employee._id} className="p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-gray-900">
                          {item.employee.teamMemberName || item.employee.name}
                        </div>
                        <div className="text-lg font-bold text-blue-600">
                          {item.assignedCount} leads
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span>{item.employee.role}</span>
                        <span>{item.percentage}% of total</span>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>

                      <div className="mt-2 text-xs text-gray-500">
                        Current workload: {item.employee.currentWorkload} | Performance: {item.employee.performanceScore}%
                      </div>
                    </div>
                  ))}

                  {/* Summary */}
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <InformationCircleIcon className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-blue-900">Assignment Summary</span>
                    </div>
                    <div className="text-sm text-blue-800">
                      <div>Total Leads: {leadsToAssign.filter(l => l.include).length} (of {leadsToAssign.length} selected)</div>
                      <div>Selected Employees: {selectedEmployees.length}</div>
                      <div>Distribution Method: {distributionMethod.charAt(0).toUpperCase() + distributionMethod.slice(1)}</div>
                      <div>Priority Level: {priority.charAt(0).toUpperCase() + priority.slice(1)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-4">
            {error && (
              <div className="flex items-center space-x-2 text-red-600">
                <ExclamationTriangleIcon className="h-5 w-5" />
                <span className="text-sm">{error}</span>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkAssignment}
              disabled={loading || selectedEmployees.length === 0 || loadingData}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {loading ? 'Assigning...' : `Assign to ${selectedEmployees.length} Employee${selectedEmployees.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkLeadAssignmentModal;
